/* ==================================================
   PEGUU — MODALES DE AUTENTICACIÓN
================================================== */

(() => {
  "use strict";


  /* ==================================================
     CONFIGURACIÓN
  ================================================== */

  const AUTH_STORAGE_KEY =
    "peguu:customer-auth";

  const SHOPIFY_LOGIN_PENDING_KEY =
    "peguu:shopify-login-pending";

  const DEFAULT_API_BASE_URL =
    window.PeguuApi.endpoint("customers");

  const PASSWORD_PATTERN =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

  let shopifySessionRequest =
    null;


  /* ==================================================
     UTILIDADES GENERALES
  ================================================== */

  function normalizeEmail(value) {
    return String(value || "")
      .trim()
      .toLowerCase();
  }


  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      .test(
        normalizeEmail(value)
      );
  }


  function isUsableCustomerName(
    value,
    email = ""
  ) {

    const normalizedValue =
      String(value || "")
        .trim();


    if (
      !normalizedValue ||
      normalizedValue.toLowerCase() ===
        "cliente" ||
      normalizedValue.toLowerCase() ===
        normalizeEmail(email) ||
      isValidEmail(normalizedValue)
    ) {
      return false;
    }


    return true;

  }


  function getErrorMessage(
    error,
    fallbackMessage
  ) {

    if (
      error &&
      typeof error.message === "string" &&
      error.message.trim()
    ) {

      return error.message.trim();

    }


    return fallbackMessage;

  }


  /* ==================================================
     LEER SESIÓN
  ================================================== */

  function readCustomerSession() {

    if (
      window.PeguuAuthClient &&
      typeof window.PeguuAuthClient.getSession ===
        "function"
    ) {
      return window.PeguuAuthClient.getSession();
    }

    try {

      const stored =
        localStorage.getItem(
          AUTH_STORAGE_KEY
        );


      if (
        !stored
      ) {

        return null;

      }


      const session =
        JSON.parse(
          stored
        );


      if (
        !session?.token ||
        !session?.customer
      ) {

        return null;

      }


      return session;

    } catch (error) {

      return null;

    }

  }


  /* ==================================================
     COMPROBAR AUTENTICACIÓN
  ================================================== */

  function isCustomerAuthenticated() {

    const session =
      readCustomerSession();


    return Boolean(
      session?.token &&
      session?.customer
    );

  }


  /* ==================================================
     RESPUESTA DE LA API
  ================================================== */

  async function parseApiResponse(
    response
  ) {

    let payload = null;


    try {

      payload =
        await response.json();

    } catch (error) {

      payload = null;

    }


    if (
      !response.ok ||
      !payload?.success
    ) {

      const apiError =
        new Error(
          payload?.message ||
          "No fue posible completar la solicitud."
        );


      apiError.status =
        response.status;


      apiError.code =
        payload?.code ||
        null;


      throw apiError;

    }


    return payload;

  }


  /* ==================================================
     ACTUALIZAR HEADER
  ================================================== */

  function updateHeaderAuthState() {

    const session =
      readCustomerSession();


    const isLoggedIn =
      Boolean(
        session?.token &&
        session?.customer
      );


    const customerEmail =
      session?.customer?.email ||
      "";


    const customerName =
      [
        session?.customer?.firstName,
        session?.customer?.name,
        session?.customer?.fullName
      ]
        .find(
          (
            value
          ) =>
            isUsableCustomerName(
              value,
              customerEmail
            )
        ) ||
      "Cliente";


    const normalizedCustomerName =
      customerName.trim();


    const initial =
      normalizedCustomerName
        .charAt(0)
        .toUpperCase();


    /* =========================
       OCULTAR / MOSTRAR
       CREAR CUENTA
    ========================== */

    document
      .querySelectorAll(
        "[data-peguu-auth-register-link]"
      )
      .forEach(
        (
          element
        ) => {

          element.hidden =
            isLoggedIn;

        }
      );


    /* =========================
       ÍCONO / ENLACE DE CUENTA
    ========================== */

    document
      .querySelectorAll(
        "[data-peguu-auth-account-trigger]"
      )
      .forEach(
        (
          element
        ) => {

          element.dataset
            .peguuAuthOpen =
            isLoggedIn
              ? "account"
              : "login";


          element.setAttribute(
            "aria-label",
            isLoggedIn
              ? `Abrir cuenta de ${
                  normalizedCustomerName ||
                  "cliente"
                }`
              : "Iniciar sesión"
          );


          /*
           * Cambiar texto únicamente
           * en el enlace del drawer.
           *
           * El ícono de escritorio
           * no tiene texto.
           */

          if (
            element.matches(
              ".peguu-header__drawer a"
            )
          ) {

            element.textContent =
              isLoggedIn
                ? "Mi cuenta"
                : "Iniciar sesión";

          }

        }
      );


    document
      .querySelectorAll(
        "[data-peguu-account-button]"
      )
      .forEach(
        (
          element
        ) => {

          const guestState =
            element.querySelector(
              "[data-peguu-account-guest]"
            );


          const avatarState =
            element.querySelector(
              "[data-peguu-account-avatar]"
            );


          const initialElement =
            element.querySelector(
              "[data-peguu-account-initial]"
            );


          if (guestState) {
            guestState.hidden =
              isLoggedIn;
          }


          if (avatarState) {
            avatarState.hidden =
              !isLoggedIn;
          }


          if (initialElement) {
            initialElement.textContent =
              isLoggedIn
                ? initial
                : "";
          }

        }
      );


    document.documentElement
      .classList
      .toggle(
        "peguu-customer-authenticated",
        isLoggedIn
      );

  }


  /* ==================================================
     GUARDAR SESIÓN
  ================================================== */

  function saveCustomerSession(
    authData
  ) {

    if (
      !authData?.token ||
      !authData?.customer
    ) {

      throw new Error(
        "La respuesta de autenticación está incompleta."
      );

    }


    if (
      window.PeguuAuthClient &&
      typeof window.PeguuAuthClient.saveSession ===
        "function"
    ) {
      return window.PeguuAuthClient.saveSession(authData);
    }

    const session = {

      token:
        authData.token,

      customer:
        authData.customer,

      savedAt:
        new Date()
          .toISOString()

    };


    localStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify(
        session
      )
    );


    updateHeaderAuthState();


    window.dispatchEvent(
      new CustomEvent(
        "peguu:customer-authenticated",
        {
          detail:
            session
        }
      )
    );


    window.dispatchEvent(
      new CustomEvent(
        "peguu:auth-state-changed",
        {
          detail: {
            authenticated:
              true,

            session
          }
        }
      )
    );


    return session;

  }


  /* ==================================================
     ELIMINAR SESIÓN
  ================================================== */

  function removeCustomerSession() {

    if (
      window.PeguuAuthClient &&
      typeof window.PeguuAuthClient.clearSession ===
        "function"
    ) {
      window.PeguuAuthClient.clearSession();
      return;
    }

    localStorage.removeItem(
      AUTH_STORAGE_KEY
    );


    updateHeaderAuthState();


    window.dispatchEvent(
      new CustomEvent(
        "peguu:customer-logged-out"
      )
    );


    window.dispatchEvent(
      new CustomEvent(
        "peguu:auth-state-changed",
        {
          detail: {
            authenticated:
              false,

            session:
              null
          }
        }
      )
    );

  }


  /* ==================================================
     INICIALIZAR MODAL
  ================================================== */

  function initializeAuthModal(
    root
  ) {

    if (
      !root ||
      root.dataset
        .peguuAuthInitialized ===
        "true"
    ) {

      return;

    }


    root.dataset
      .peguuAuthInitialized =
      "true";


    /* ==================================================
       ELEMENTOS GENERALES
    ================================================== */

    const dialog =
      root.querySelector(
        ".peguu-auth__dialog"
      );


    const views =
      Array.from(
        root.querySelectorAll(
          "[data-peguu-auth-view]"
        )
      );


    const closeButtons =
      root.querySelectorAll(
        "[data-peguu-auth-close]"
      );


    const navigationButtons =
      root.querySelectorAll(
        "[data-peguu-auth-go]"
      );


    const passwordButtons =
      root.querySelectorAll(
        "[data-peguu-password-toggle]"
      );


    const recoveryButton =
      root.querySelector(
        "[data-peguu-auth-recovery]"
      );


    const shopifyLoginButton =
      root.querySelector(
        "[data-peguu-shopify-login]"
      );


    const shopifyLoginLabel =
      root.querySelector(
        "[data-peguu-shopify-login-label]"
      );


    /* ==================================================
       FORMULARIOS
    ================================================== */

    const loginForm =
      root.querySelector(
        "[data-peguu-login-form]"
      );


    const registerForm =
      root.querySelector(
        "[data-peguu-register-form]"
      );


    const loginMessage =
      root.querySelector(
        "[data-peguu-login-message]"
      );


    const registerMessage =
      root.querySelector(
        "[data-peguu-register-message]"
      );


    const loginSubmit =
      root.querySelector(
        "[data-peguu-login-submit]"
      );


    const registerSubmit =
      root.querySelector(
        "[data-peguu-register-submit]"
      );


    const loginSubmitLabel =
      root.querySelector(
        "[data-peguu-login-submit-label]"
      );


    const registerSubmitLabel =
      root.querySelector(
        "[data-peguu-register-submit-label]"
      );


    const registerPasswordInput =
      registerForm
        ?.querySelector(
          "[data-peguu-register-password]"
        );


    const registerPasswordConfirmInput =
      registerForm
        ?.querySelector(
          "[data-peguu-register-password-confirm]"
        );


    const passwordRequirementsMessage =
      root.querySelector(
        "[data-peguu-password-requirements]"
      );


    const passwordMatchMessage =
      root.querySelector(
        "[data-peguu-password-match]"
      );


    /* ==================================================
       VISTA DE CUENTA
    ================================================== */

    const accountName =
      root.querySelector(
        "[data-peguu-account-name]"
      );


    const accountTitlePrefix =
      root.querySelector(
        "[data-peguu-account-title-prefix]"
      );


    const accountDescription =
      root.querySelector(
        "[data-peguu-account-description]"
      );


    const accountFullName =
      root.querySelector(
        "[data-peguu-account-full-name]"
      );


    const accountEmail =
      root.querySelector(
        "[data-peguu-account-email]"
      );


    const accountCard =
      root.querySelector(
        "[data-peguu-account-card]"
      );


    const profileForm =
      root.querySelector(
        "[data-peguu-profile-form]"
      );


    const profileFirstNameInput =
      root.querySelector(
        "[data-peguu-profile-first-name]"
      );


    const profileLastNameInput =
      root.querySelector(
        "[data-peguu-profile-last-name]"
      );


    const profileSubmit =
      root.querySelector(
        "[data-peguu-profile-submit]"
      );


    const profileSubmitLabel =
      root.querySelector(
        "[data-peguu-profile-submit-label]"
      );


    const profileMessage =
      root.querySelector(
        "[data-peguu-profile-message]"
      );


    const logoutButton =
      root.querySelector(
        "[data-peguu-auth-logout]"
      );


    const accountVerificationNotice =
      root.querySelector(
        "[data-peguu-account-verification-notice]"
      );


    const resendVerificationButton =
      root.querySelector(
        "[data-peguu-account-resend-verification]"
      );


    /* ==================================================
       API
    ================================================== */

    const apiBaseUrl =
      window.PeguuApi.resolve(
        root.dataset.apiBaseUrl ||
        DEFAULT_API_BASE_URL
      )
        .replace(
          /\/+$/,
          ""
        );


    const shopifyProxyLoginUrl =
      String(
        root.dataset
          .shopifyProxyLoginUrl ||
        "/apps/peguu/customers/login"
      );


    const shopifyLogoutUrl =
      String(
        root.dataset
          .shopifyLogoutUrl ||
        ""
      );


    const shopifyCustomerLoggedIn =
      root.dataset
        .shopifyCustomerLoggedIn ===
      "true";


    const shopifyCustomerFirstName =
      String(
        root.dataset
          .shopifyCustomerFirstName ||
        ""
      )
        .trim();


    const shopifyCustomerLastName =
      String(
        root.dataset
          .shopifyCustomerLastName ||
        ""
      )
        .trim();


    let activeViewName =
      "offer";


    let accountViewMode =
      "profile";


    let lastFocusedElement =
      null;


    /* ==================================================
       MENSAJES
    ================================================== */

    function hideMessage(
      element
    ) {

      if (
        !element
      ) {

        return;

      }


      element.hidden =
        true;


      element.textContent =
        "";


      element.classList.remove(
        "is-error",
        "is-success",
        "is-info"
      );

    }


    function showMessage(
      element,
      message,
      type = "error"
    ) {

      if (
        !element
      ) {

        return;

      }


      element.hidden =
        false;


      element.textContent =
        message;


      element.classList.remove(
        "is-error",
        "is-success",
        "is-info"
      );


      element.classList.add(
        `is-${type}`
      );

    }


    function clearMessages() {

      hideMessage(
        loginMessage
      );


      hideMessage(
        registerMessage
      );


      hideMessage(
        profileMessage
      );

    }


    /* ==================================================
       VALIDACIÓN DE CONTRASEÑAS EN TIEMPO REAL
    ================================================== */

    function updateRegisterPasswordValidation() {

      const password =
        String(
          registerPasswordInput
            ?.value ||
          ""
        );


      const passwordConfirmation =
        String(
          registerPasswordConfirmInput
            ?.value ||
          ""
        );


      if (
        passwordRequirementsMessage
      ) {

        passwordRequirementsMessage.hidden =
          password.length === 0 ||
          PASSWORD_PATTERN.test(
            password
          );

      }


      if (
        passwordMatchMessage
      ) {

        passwordMatchMessage.hidden =
          passwordConfirmation.length ===
            0 ||
          password ===
            passwordConfirmation;

      }

    }


    /* ==================================================
       RESTABLECER OJOS DE CONTRASEÑA
    ================================================== */

    function resetPasswordVisibility() {

      passwordButtons
        .forEach(
          (
            button
          ) => {

            const targetId =
              button.dataset.target;


            const input =
              document.getElementById(
                targetId
              );


            if (
              input
            ) {

              input.type =
                "password";

            }


            button.setAttribute(
              "aria-pressed",
              "false"
            );


            button.setAttribute(
              "aria-label",
              "Mostrar contraseña"
            );


            const eyeSlash =
              button.querySelector(
                "[data-password-eye-slash]"
              );


            if (
              eyeSlash
            ) {

              eyeSlash.style.display =
                "";

            }

          }
        );

    }


    /* ==================================================
       LLENAR VISTA DE CUENTA
    ================================================== */

    function fillAccountView() {

      const session =
        readCustomerSession();


      const customer =
        session?.customer;


      if (
        !customer
      ) {

        return false;

      }


      const rawCustomerFirstName =
        String(
          customer.firstName ||
          customer.first_name ||
          shopifyCustomerFirstName ||
          ""
        )
          .trim();


      const rawCustomerLastName =
        String(
          customer.lastName ||
          customer.last_name ||
          shopifyCustomerLastName ||
          ""
        )
          .trim();


      const email =
        String(
          customer.email ||
          "—"
        )
          .trim();


      const customerFirstName =
        isUsableCustomerName(
          rawCustomerFirstName,
          email
        )
          ? rawCustomerFirstName
          : "";


      const customerLastName =
        isUsableCustomerName(
          rawCustomerLastName,
          email
        )
          ? rawCustomerLastName
          : "";


      const providedFullName =
        String(
          customer.fullName ||
          customer.name ||
          ""
        )
          .trim();


      const fullNameFromParts =
        [
          customerFirstName,
          customerLastName
        ]
          .filter(Boolean)
          .join(" ");


      const fullName =
        fullNameFromParts ||
        (
          providedFullName &&
          providedFullName.toLowerCase() !==
            email.toLowerCase()
            ? providedFullName
            : "Cliente"
        );


      const firstName =
        customerFirstName ||
        fullName
          .split(/\s+/)
          .filter(Boolean)[0] ||
        "Cliente";


      const hasRealName =
        Boolean(
          fullNameFromParts ||
          isUsableCustomerName(
            providedFullName,
            email
          )
        );


      const needsProfile =
        customer.needsProfile ===
          true ||
        !hasRealName;


      if (
        accountTitlePrefix
      ) {
        accountTitlePrefix.textContent =
          needsProfile
            ? "Completa"
            : "Hola,";
      }


      if (
        accountDescription
      ) {
        accountDescription.textContent =
          needsProfile
            ? "Agrega tu nombre para personalizar tu cuenta."
            : "Tu sesión está activa.";
      }


      if (
        accountName
      ) {

        accountName.textContent =
          needsProfile
            ? "tu perfil"
            : firstName;

      }


      if (
        accountFullName
      ) {

        accountFullName.textContent =
          fullName;

      }


      if (
        accountEmail
      ) {

        accountEmail.textContent =
          email;

      }


      if (
        profileFirstNameInput
      ) {
        profileFirstNameInput.value =
          customerFirstName;
      }


      if (
        profileLastNameInput
      ) {
        profileLastNameInput.value =
          customerLastName;
      }


      if (
        profileForm
      ) {
        profileForm.hidden =
          !needsProfile;
      }


      if (
        accountCard
      ) {
        accountCard.hidden =
          needsProfile;
      }


      syncAccountVerificationNotice(
        customer
      );


      return true;

    }


    function isCustomerEmailVerified(
      customer
    ) {

      return customer?.emailVerified === true;

    }


    function syncAccountVerificationNotice(
      customer
    ) {

      const needsVerification =
        Boolean(
          customer
        ) &&
        !isCustomerEmailVerified(
          customer
        );


      if (
        accountVerificationNotice
      ) {

        accountVerificationNotice.hidden =
          !needsVerification;

      }


      if (
        resendVerificationButton
      ) {

        resendVerificationButton.hidden =
          !needsVerification;

        resendVerificationButton.disabled =
          false;

      }

    }


    async function refreshCustomerProfile() {

      const session =
        readCustomerSession();


      if (!session?.token) {
        return null;
      }


      const response =
        await window.PeguuAuthClient.authFetch(
          `${apiBaseUrl}/me`,
          {
            method: "GET",

            headers: {
              Accept:
                "application/json",

              Authorization:
                `Bearer ${session.token}`
            }
          }
        );


      const payload =
        await parseApiResponse(
          response
        );


      const customer =
        payload?.data?.customer ||
        payload?.data;


      if (!customer) {
        return session;
      }


      const nextSession =
        Object.assign(
          session,
          {
            customer: {
              ...session.customer,
              ...customer
            }
          }
        );


      localStorage.setItem(
        AUTH_STORAGE_KEY,
        JSON.stringify(
          nextSession
        )
      );


      updateHeaderAuthState();

      fillAccountView();


      window.dispatchEvent(
        new CustomEvent(
          "peguu:customer-updated",
          {
            detail:
              nextSession
          }
        )
      );


      return nextSession;

    }


    async function requestEmailVerification() {

      const session =
        readCustomerSession();


      if (!session?.token) {
        throw new Error(
          "Inicia sesión para verificar tu correo."
        );
      }


      const response =
        await window.PeguuAuthClient.authFetch(
          `${apiBaseUrl}/request-email-verification`,
          {
            method: "POST",

            headers: {
              Accept:
                "application/json",

              Authorization:
                `Bearer ${session.token}`
            }
          }
        );


      await parseApiResponse(
        response
      );

    }


    /* ==================================================
       MODO DE LA VISTA DE CUENTA
    ================================================== */

    function setAccountViewMode(
      mode
    ) {

      accountViewMode =
        mode === "register-welcome"
          ? "register-welcome"
          : (
              mode === "welcome"
                ? "welcome"
                : "profile"
            );


      if (
        logoutButton
      ) {

        const sessionCustomer =
          readCustomerSession()
            ?.customer;


        const sessionFullName =
          String(
            sessionCustomer?.fullName ||
            sessionCustomer?.name ||
            ""
          )
            .trim();


        const sessionEmail =
          String(
            sessionCustomer?.email ||
            ""
          )
            .trim();


        const hasSessionName =
          Boolean(
            isUsableCustomerName(
              sessionCustomer?.firstName ||
              sessionCustomer?.first_name,
              sessionEmail
            ) ||
            isUsableCustomerName(
              sessionFullName,
              sessionEmail
            )
          );


        const needsProfile =
          sessionCustomer?.needsProfile ===
            true ||
          !hasSessionName;

        logoutButton.hidden =
          accountViewMode !==
            "profile" &&
          !needsProfile;

      }

    }


    /* ==================================================
       MOSTRAR VISTA
    ================================================== */

    function showView(
      viewName,
      accountMode
    ) {

      const requestedViewName =
        viewName;

      /*
       * Si el cliente está logueado,
       * no se muestran oferta,
       * registro ni login.
       */

      if (
        isCustomerAuthenticated() &&
        (
          viewName === "offer" ||
          viewName === "login" ||
          viewName === "register"
        )
      ) {

        viewName =
          "account";

      }


      /*
       * Si se pide la cuenta
       * pero no hay sesión,
       * mostrar login.
       */

      if (
        viewName === "account" &&
        !fillAccountView()
      ) {

        viewName =
          "login";

      }


      if (
        viewName === "account"
      ) {

        setAccountViewMode(
          accountMode ||
          (
            requestedViewName ===
            "account"
              ? "profile"
              : "welcome"
          )
        );

      }


      const requestedView =
        root.querySelector(
          `[data-peguu-auth-view="${viewName}"]`
        );


      if (
        !requestedView
      ) {

        return;

      }


      activeViewName =
        viewName;


      clearMessages();


      resetPasswordVisibility();


      views.forEach(
        (
          view
        ) => {

          const isActive =
            view.dataset
              .peguuAuthView ===
            viewName;


          view.hidden =
            !isActive;

        }
      );


      if (
        dialog
      ) {

        dialog.scrollTop =
          0;

      }


      window.setTimeout(
        () => {

          const firstInput =
            requestedView
              .querySelector(
                "input"
              );


          const firstButton =
            requestedView
              .querySelector(
                "button:not([data-peguu-auth-close]):not([hidden])"
              );


          (
            firstInput ||
            firstButton
          )
            ?.focus();

        },
        70
      );

    }


    /* ==================================================
       ABRIR MODAL
    ================================================== */

    function openModal(
      viewName = "offer"
    ) {

      const requestedViewName =
        viewName;

      /*
       * Cualquier intento de abrir
       * login, registro u oferta
       * abre cuenta si hay sesión.
       */

      if (
        isCustomerAuthenticated() &&
        (
          viewName === "offer" ||
          viewName === "login" ||
          viewName === "register"
        )
      ) {

        viewName =
          "account";

      }


      if (
        viewName === "account" &&
        !fillAccountView()
      ) {

        viewName =
          "login";

      }


      lastFocusedElement =
        document.activeElement
          instanceof HTMLElement
          ? document.activeElement
          : null;


      root.classList.add(
        "is-open"
      );


      root.setAttribute(
        "aria-hidden",
        "false"
      );


      document.body
        .classList
        .add(
          "peguu-auth-open"
        );


      showView(
        viewName,
        viewName === "account"
          ? (
              requestedViewName ===
              "account"
                ? "profile"
                : "welcome"
            )
          : undefined
      );

    }


    /* ==================================================
       CERRAR MODAL
    ================================================== */

    function closeModal() {

      const activeElement =
        document.activeElement;


      if (
        lastFocusedElement &&
        lastFocusedElement.isConnected &&
        !root.contains(
          lastFocusedElement
        ) &&
        typeof lastFocusedElement
          .focus ===
          "function"
      ) {

        lastFocusedElement
          .focus();

      }


      if (
        root.contains(
          document.activeElement
        ) &&
        activeElement instanceof
          HTMLElement
      ) {

        activeElement.blur();

      }


      root.classList.remove(
        "is-open"
      );


      root.setAttribute(
        "aria-hidden",
        "true"
      );


      document.body
        .classList
        .remove(
          "peguu-auth-open"
        );


      clearMessages();


      resetPasswordVisibility();

    }


    /* ==================================================
       CARGA LOGIN
    ================================================== */

    function setLoginLoading(
      isLoading
    ) {

      if (
        !loginSubmit ||
        !loginSubmitLabel
      ) {

        return;

      }


      loginSubmit.disabled =
        isLoading;


      loginSubmitLabel
        .textContent =
        isLoading
          ? "INICIANDO..."
          : "EMPIEZA A PEGAR";

    }


    function setShopifyLoginLoading(
      isLoading
    ) {

      if (
        !shopifyLoginButton ||
        !shopifyLoginLabel
      ) {

        return;

      }


      shopifyLoginButton.disabled =
        isLoading;


      shopifyLoginLabel.textContent =
        isLoading
          ? "CONECTANDO..."
          : "CONTINUAR CON GOOGLE";

    }


    /* ==================================================
       CARGA REGISTRO
    ================================================== */

    function setRegisterLoading(
      isLoading
    ) {

      if (
        !registerSubmit ||
        !registerSubmitLabel
      ) {

        return;

      }


      registerSubmit.disabled =
        isLoading;


      registerSubmitLabel
        .textContent =
        isLoading
          ? "CREANDO CUENTA..."
          : "EMPIEZA A PEGAR";

    }


    function setProfileLoading(
      isLoading
    ) {

      if (
        !profileSubmit ||
        !profileSubmitLabel
      ) {
        return;
      }


      profileSubmit.disabled =
        isLoading;


      profileSubmitLabel.textContent =
        isLoading
          ? "GUARDANDO..."
          : "GUARDAR NOMBRE";

    }


    /* ==================================================
       MOSTRAR / OCULTAR CONTRASEÑA
    ================================================== */

    function handlePasswordToggle(
      button
    ) {

      const targetId =
        button.dataset.target;


      const input =
        document.getElementById(
          targetId
        );


      if (
        !input
      ) {

        return;

      }


      const shouldShow =
        input.type ===
        "password";


      input.type =
        shouldShow
          ? "text"
          : "password";


      button.setAttribute(
        "aria-pressed",
        String(
          shouldShow
        )
      );


      button.setAttribute(
        "aria-label",
        shouldShow
          ? "Ocultar contraseña"
          : "Mostrar contraseña"
      );


      const eyeSlash =
        button.querySelector(
          "[data-password-eye-slash]"
        );


      if (
        eyeSlash
      ) {

        eyeSlash.style.display =
          shouldShow
            ? "none"
            : "";

      }


      input.focus();


      try {

        const length =
          input.value.length;


        input.setSelectionRange(
          length,
          length
        );

      } catch (error) {

        /*
         * Algunos navegadores pueden
         * impedir setSelectionRange.
         */

      }

    }


    /* ==================================================
       LOGIN
    ================================================== */

    async function loginCustomer(
      credentials
    ) {

      const email =
        normalizeEmail(
          credentials?.email
        );


      const password =
        String(
          credentials?.password ||
          ""
        );


      if (!email || !password) {
        throw new Error(
          "Completa tu email y contraseña."
        );
      }


      if (!isValidEmail(email)) {
        throw new Error(
          "Ingresa un correo electrónico válido."
        );
      }


      const response =
        await fetch(
          `${apiBaseUrl}/login`,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              Accept:
                "application/json"
            },

            body:
              JSON.stringify({
                email,
                password
              })
          }
        );


      const payload =
        await parseApiResponse(
          response
        );


      const session =
        saveCustomerSession(
          payload.data
        );


      fillAccountView();


      return session;

    }


    async function loginCustomerWithShopify() {

      if (!shopifySessionRequest) {

        shopifySessionRequest =
          fetch(
            shopifyProxyLoginUrl,
            {
              method: "POST",

              headers: {
                Accept:
                  "application/json"
              },

              credentials:
                "same-origin"
            }
          )
            .then(
              parseApiResponse
            )
            .catch(
              (
                error
              ) => {

                shopifySessionRequest =
                  null;


                throw error;

              }
            );

      }


      const payload =
        await shopifySessionRequest;


      shopifySessionRequest =
        null;


      const shopifyCustomer =
        payload.data.customer ||
        {};


      const shopifyEmail =
        String(
          shopifyCustomer.email ||
          ""
        )
          .trim();


      const rawFirstName =
        String(
          shopifyCustomer.firstName ||
          shopifyCustomer.first_name ||
          shopifyCustomerFirstName ||
          ""
        )
          .trim();


      const rawLastName =
        String(
          shopifyCustomer.lastName ||
          shopifyCustomer.last_name ||
          shopifyCustomerLastName ||
          ""
        )
          .trim();


      const firstName =
        isUsableCustomerName(
          rawFirstName,
          shopifyEmail
        )
          ? rawFirstName
          : "";


      const lastName =
        isUsableCustomerName(
          rawLastName,
          shopifyEmail
        )
          ? rawLastName
          : "";


      const fullName =
        [
          firstName,
          lastName
        ]
          .filter(Boolean)
          .join(" ");


      const session =
        saveCustomerSession(
          {
            ...payload.data,

            customer: {
              ...shopifyCustomer,

              firstName:
                firstName ||
                undefined,

              lastName:
                lastName ||
                undefined,

              fullName:
                fullName ||
                shopifyCustomer.fullName
            }
          }
        );


      fillAccountView();


      return session;

    }


    function redirectToShopifyLogin({
      openModalAfterLogin = true
    } = {}) {

      const loginUrl =
        String(
          root.dataset
            .shopifyLoginUrl ||
          ""
        );


      if (!loginUrl) {
        throw new Error(
          "El acceso con Shopify no está disponible."
        );
      }


      if (openModalAfterLogin) {
        sessionStorage.setItem(
          SHOPIFY_LOGIN_PENDING_KEY,
          "true"
        );
      } else {
        sessionStorage.removeItem(
          SHOPIFY_LOGIN_PENDING_KEY
        );
      }


      window.location.assign(
        loginUrl
      );

    }


    async function handleShopifyLogin() {

      hideMessage(
        loginMessage
      );


      if (!shopifyCustomerLoggedIn) {

        try {
          redirectToShopifyLogin();
        } catch (error) {
          showMessage(
            loginMessage,
            getErrorMessage(
              error,
              "No fue posible abrir el acceso con Shopify."
            )
          );
        }


        return;

      }


      setShopifyLoginLoading(
        true
      );


      try {

        await loginCustomerWithShopify();


        showView(
          "account",
          "welcome"
        );

      } catch (error) {

        showMessage(
          loginMessage,
          getErrorMessage(
            error,
            "No fue posible conectar tu cuenta de Shopify con Peguu."
          )
        );

      } finally {

        setShopifyLoginLoading(
          false
        );

      }

    }


    async function synchronizeShopifySession() {

      const shouldOpenAfterLogin =
        sessionStorage.getItem(
          SHOPIFY_LOGIN_PENDING_KEY
        ) ===
        "true";


      if (
        !shopifyCustomerLoggedIn
      ) {

        return;

      }


      if (
        isCustomerAuthenticated()
      ) {
        if (
          shouldOpenAfterLogin
        ) {
          sessionStorage.removeItem(
            SHOPIFY_LOGIN_PENDING_KEY
          );


          openModal(
            "login"
          );
        }


        return;
      }


      try {

        await loginCustomerWithShopify();


        if (
          shouldOpenAfterLogin
        ) {
          sessionStorage.removeItem(
            SHOPIFY_LOGIN_PENDING_KEY
          );


          openModal(
            "login"
          );
        }

      } catch (error) {

        if (
          shouldOpenAfterLogin
        ) {
          sessionStorage.removeItem(
            SHOPIFY_LOGIN_PENDING_KEY
          );


          openModal(
            "login"
          );


          showMessage(
            loginMessage,
            getErrorMessage(
              error,
              "No fue posible conectar la sesión de Shopify."
            )
          );
        }

        window.dispatchEvent(
          new CustomEvent(
            "peguu:shopify-auth-error",
            {
              detail: {
                message:
                  getErrorMessage(
                    error,
                    "No fue posible conectar la sesión de Shopify."
                  ),

                status:
                  error?.status ||
                  null
              }
            }
          )
        );

      }

    }


    async function validateCustomerSession() {

      const session =
        readCustomerSession();


      if (!session) {
        return null;
      }


      try {

        const response =
          await window.PeguuAuthClient.authFetch(
            `${apiBaseUrl}/me/cart`,
            {
              method: "GET",

              headers: {
                Accept:
                  "application/json",

                Authorization:
                  `Bearer ${session.token}`
              }
            }
          );


        if (
          response.status === 401
        ) {
          const sessionError =
            new Error(
              "Tu sesión expiró. Inicia sesión nuevamente."
            );


          sessionError.status =
            response.status;


          throw sessionError;
        }


        await parseApiResponse(
          response
        );


        return session;

      } catch (error) {

        if (
          error?.status === 401
        ) {
          removeCustomerSession();

          const sessionError =
            new Error(
              "Tu sesión expiró. Inicia sesión nuevamente."
            );


          sessionError.status =
            error.status;


          throw sessionError;
        }


        throw error;

      }

    }

    async function handleLogin(
      event
    ) {

      event.preventDefault();


      hideMessage(
        loginMessage
      );


      const emailInput =
        loginForm
          ?.querySelector(
            "[data-peguu-login-email]"
          );


      const passwordInput =
        loginForm
          ?.querySelector(
            "[data-peguu-login-password]"
          );


      const email =
        normalizeEmail(
          emailInput?.value
        );


      const password =
        String(
          passwordInput?.value ||
          ""
        );


      if (
        !email ||
        !password
      ) {

        showMessage(
          loginMessage,
          "Completa tu email y contraseña."
        );


        return;

      }


      if (
        !isValidEmail(
          email
        )
      ) {

        showMessage(
          loginMessage,
          "Ingresa un correo electrónico válido."
        );


        emailInput
          ?.focus();


        return;

      }


      setLoginLoading(
        true
      );


      try {

        const session =
          await loginCustomer({
            email,
            password
          });


        showMessage(
          loginMessage,
          `¡Bienvenido de vuelta, ${
            session.customer
              .fullName ||
            "cliente"
          }!`,
          "success"
        );


        loginForm.reset();


        resetPasswordVisibility();


        window.setTimeout(
          () => {

            showView(
              "account",
              "welcome"
            );

          },
          700
        );

      } catch (error) {

        let message =
          getErrorMessage(
            error,
            "No fue posible iniciar sesión."
          );


        if (
          error?.status ===
          401
        ) {

          message =
            "El correo o la contraseña son incorrectos.";

        }


        if (
          error?.status ===
          429
        ) {

          message =
            "Has realizado demasiados intentos. Espera un momento e inténtalo nuevamente.";

        }


        if (
          error?.status ===
          503
        ) {

          message =
            "El servicio no está disponible temporalmente. Inténtalo más tarde.";

        }


        showMessage(
          loginMessage,
          message
        );

      } finally {

        setLoginLoading(
          false
        );

      }

    }


    /* ==================================================
       REGISTRO
    ================================================== */

    async function handleRegister(
      event
    ) {

      event.preventDefault();


      hideMessage(
        registerMessage
      );


      const nameInput =
        registerForm
          ?.querySelector(
            "[data-peguu-register-name]"
          );


      const emailInput =
        registerForm
          ?.querySelector(
            "[data-peguu-register-email]"
          );


      const passwordInput =
        registerForm
          ?.querySelector(
            "[data-peguu-register-password]"
          );


      const passwordConfirmInput =
        registerForm
          ?.querySelector(
            "[data-peguu-register-password-confirm]"
          );


      const fullName =
        String(
          nameInput?.value ||
          ""
        )
          .trim();


      const email =
        normalizeEmail(
          emailInput?.value
        );


      const password =
        String(
          passwordInput?.value ||
          ""
        );


      const passwordConfirmation =
        String(
          passwordConfirmInput
            ?.value ||
          ""
        );


      if (
        !fullName ||
        !email ||
        !password ||
        !passwordConfirmation
      ) {

        showMessage(
          registerMessage,
          "Completa todos los campos para crear tu cuenta."
        );


        return;

      }


      if (
        fullName.length <
        2
      ) {

        showMessage(
          registerMessage,
          "Ingresa tu nombre completo."
        );


        nameInput
          ?.focus();


        return;

      }


      if (
        !isValidEmail(
          email
        )
      ) {

        showMessage(
          registerMessage,
          "Ingresa un correo electrónico válido."
        );


        emailInput
          ?.focus();


        return;

      }


      if (
        !PASSWORD_PATTERN
          .test(
            password
          )
      ) {

        showMessage(
          registerMessage,
          "La contraseña debe tener mínimo 8 caracteres, una mayúscula, una minúscula y un número."
        );


        passwordInput
          ?.focus();


        return;

      }


      if (
        password !==
        passwordConfirmation
      ) {

        showMessage(
          registerMessage,
          "Las contraseñas no coinciden."
        );


        passwordConfirmInput
          ?.focus();


        return;

      }


      setRegisterLoading(
        true
      );


      try {

        const response =
          await fetch(
            `${apiBaseUrl}/register`,
            {
              method:
                "POST",

              headers: {
                "Content-Type":
                  "application/json",

                Accept:
                  "application/json"
              },

              body:
                JSON.stringify(
                  {
                    fullName,
                    email,
                    password
                  }
                )
            }
          );


        const payload =
          await parseApiResponse(
            response
          );


        const session =
          saveCustomerSession(
            payload.data
          );


        fillAccountView();


        showMessage(
          registerMessage,
          `¡Cuenta creada! Bienvenido, ${
            session.customer
              .fullName ||
            "cliente"
          }.`,
          "success"
        );


        registerForm.reset();


        updateRegisterPasswordValidation();


        resetPasswordVisibility();


        window.setTimeout(
          () => {

            showView(
              "account",
              "register-welcome"
            );

          },
          2000
        );

      } catch (error) {

        let message =
          getErrorMessage(
            error,
            "No fue posible crear la cuenta."
          );


        if (
          error?.status ===
          409
        ) {

          message =
            "Ya existe una cuenta registrada con este correo.";

        }


        if (
          error?.status ===
          429
        ) {

          message =
            "Has realizado demasiados intentos. Espera un momento e inténtalo nuevamente.";

        }


        if (
          error?.status ===
          503
        ) {

          message =
            "El servicio no está disponible temporalmente. Inténtalo más tarde.";

        }


        showMessage(
          registerMessage,
          message
        );

      } finally {

        setRegisterLoading(
          false
        );

      }

    }


    /* ==================================================
       COMPLETAR PERFIL
    ================================================== */

    async function handleProfileSubmit(
      event
    ) {

      event.preventDefault();


      hideMessage(
        profileMessage
      );


      const session =
        readCustomerSession();


      if (
        !session?.token ||
        !session?.customer
      ) {
        showMessage(
          profileMessage,
          "Tu sesión expiró. Inicia sesión nuevamente."
        );


        return;
      }


      const firstName =
        String(
          profileFirstNameInput
            ?.value ||
          ""
        )
          .trim();


      const lastName =
        String(
          profileLastNameInput
            ?.value ||
          ""
        )
          .trim();


      if (
        !firstName ||
        !lastName
      ) {
        showMessage(
          profileMessage,
          "Ingresa tu nombre y apellido."
        );


        (
          !firstName
            ? profileFirstNameInput
            : profileLastNameInput
        )
          ?.focus();


        return;
      }


      setProfileLoading(
        true
      );


      try {

        const response =
          await window.PeguuAuthClient.authFetch(
            `${apiBaseUrl}/me`,
            {
              method: "PATCH",

              headers: {
                "Content-Type":
                  "application/json",

                Accept:
                  "application/json",

                Authorization:
                  `Bearer ${session.token}`
              },

              body:
                JSON.stringify({
                  firstName,
                  lastName
                })
            }
          );


        let payload = null;


        try {
          payload =
            await response.json();
        } catch (error) {
          payload = null;
        }


        if (
          !response.ok ||
          payload?.success ===
            false
        ) {
          const apiError =
            new Error(
              payload?.message ||
              "No fue posible guardar tu nombre."
            );


          apiError.status =
            response.status;


          throw apiError;
        }


        const updatedCustomer =
          payload?.data?.customer ||
          payload?.data ||
          payload?.customer ||
          payload;


        if (
          !updatedCustomer ||
          typeof updatedCustomer !==
            "object"
        ) {
          throw new Error(
            "La respuesta del perfil está incompleta."
          );
        }


        saveCustomerSession({
          token:
            session.token,

          customer: {
            ...session.customer,
            ...updatedCustomer,

            firstName,
            lastName,

            fullName:
              updatedCustomer.fullName ||
              `${firstName} ${lastName}`,

            needsProfile:
              false
          }
        });


        fillAccountView();


        setAccountViewMode(
          "profile"
        );


        showMessage(
          profileMessage,
          "Tu nombre se guardó correctamente.",
          "success"
        );

      } catch (error) {

        showMessage(
          profileMessage,
          getErrorMessage(
            error,
            "No fue posible guardar tu nombre."
          )
        );

      } finally {

        setProfileLoading(
          false
        );

      }

    }


    /* ==================================================
       CERRAR SESIÓN
    ================================================== */

    async function handleLogout() {

      if (
        window.PeguuAuthClient &&
        typeof window.PeguuAuthClient.logout ===
          "function"
      ) {
        await window.PeguuAuthClient.logout();
      } else {
        removeCustomerSession();
      }


      sessionStorage.removeItem(
        SHOPIFY_LOGIN_PENDING_KEY
      );


      loginForm
        ?.reset();


      registerForm
        ?.reset();


      updateRegisterPasswordValidation();


      resetPasswordVisibility();


      if (
        shopifyCustomerLoggedIn &&
        shopifyLogoutUrl
      ) {
        window.location.assign(
          shopifyLogoutUrl
        );


        return;
      }


      closeModal();

    }


    /* ==================================================
       FOCO ACCESIBLE
    ================================================== */

    function trapFocus(
      event
    ) {

      const activeView =
        root.querySelector(
          `[data-peguu-auth-view="${activeViewName}"]`
        );


      if (
        !activeView ||
        !dialog
      ) {

        return;

      }


      const focusableElements =
        Array.from(
          dialog.querySelectorAll(
            [
              "button:not([disabled])",
              "input:not([disabled])",
              "a[href]",
              "[tabindex]:not([tabindex='-1'])"
            ].join(",")
          )
        )
          .filter(
            (
              element
            ) => {

              return (
                !element.hidden &&
                element.offsetParent !==
                null
              );

            }
          );


      if (
        !focusableElements.length
      ) {

        return;

      }


      const firstElement =
        focusableElements[0];


      const lastElement =
        focusableElements[
          focusableElements.length -
          1
        ];


      if (
        event.shiftKey &&
        document.activeElement ===
        firstElement
      ) {

        event.preventDefault();


        lastElement.focus();

      } else if (
        !event.shiftKey &&
        document.activeElement ===
        lastElement
      ) {

        event.preventDefault();


        firstElement.focus();

      }

    }


    /* ==================================================
       EVENTOS INTERNOS
    ================================================== */

    closeButtons
      .forEach(
        (
          button
        ) => {

          button.addEventListener(
            "click",
            closeModal
          );

        }
      );


    navigationButtons
      .forEach(
        (
          button
        ) => {

          button.addEventListener(
            "click",
            () => {

              showView(
                button.dataset
                  .peguuAuthGo
              );

            }
          );

        }
      );


    passwordButtons
      .forEach(
        (
          button
        ) => {

          button.addEventListener(
            "click",
            () => {

              handlePasswordToggle(
                button
              );

            }
          );

        }
      );


    recoveryButton
      ?.addEventListener(
        "click",
        () => {

          showMessage(
            loginMessage,
            "La recuperación de contraseña estará disponible próximamente.",
            "info"
          );

        }
      );


    shopifyLoginButton
      ?.addEventListener(
        "click",
        handleShopifyLogin
      );


    logoutButton
      ?.addEventListener(
        "click",
        handleLogout
      );


    profileForm
      ?.addEventListener(
        "submit",
        handleProfileSubmit
      );


    resendVerificationButton
      ?.addEventListener(
        "click",
        async () => {

          if (
            resendVerificationButton.disabled
          ) {
            return;
          }


          resendVerificationButton.disabled =
            true;


          const originalText =
            resendVerificationButton.textContent;


          resendVerificationButton.textContent =
            "Enviando…";


          try {

            await requestEmailVerification();


            if (
              accountVerificationNotice
            ) {

              accountVerificationNotice.textContent =
                "Te enviamos un correo de verificación. Revisa tu bandeja de entrada o correo no deseado.";

              accountVerificationNotice.hidden =
                false;

            }


            resendVerificationButton.textContent =
              "Correo enviado";

          } catch (error) {

            resendVerificationButton.disabled =
              false;

            resendVerificationButton.textContent =
              originalText;


            if (
              accountVerificationNotice
            ) {

              accountVerificationNotice.textContent =
                error?.status === 429
                  ? "Demasiados intentos. Espera un momento y vuelve a intentar."
                  : (
                      error?.message ||
                      "No fue posible reenviar el correo de verificación."
                    );

              accountVerificationNotice.hidden =
                false;

            }

          }

        }
      );


    loginForm
      ?.addEventListener(
        "submit",
        handleLogin
      );


    registerForm
      ?.addEventListener(
        "submit",
        handleRegister
      );


    registerPasswordInput
      ?.addEventListener(
        "input",
        updateRegisterPasswordValidation
      );


    registerPasswordConfirmInput
      ?.addEventListener(
        "input",
        updateRegisterPasswordValidation
      );


    /* ==================================================
       DISPARADORES EXTERNOS
    ================================================== */

    document.addEventListener(
      "click",
      (
        event
      ) => {

        const trigger =
          event.target.closest(
            "[data-peguu-auth-open]"
          );


        if (
          !trigger
        ) {

          return;

        }


        event.preventDefault();


        const requestedView =
          trigger.dataset
            .peguuAuthOpen ||
          "offer";


        openModal(
          requestedView
        );

      }
    );


    /* ==================================================
       TECLADO
    ================================================== */

    document.addEventListener(
      "keydown",
      (
        event
      ) => {

        if (
          !root.classList.contains(
            "is-open"
          )
        ) {

          return;

        }


        if (
          event.key ===
          "Escape"
        ) {

          closeModal();


          return;

        }


        if (
          event.key ===
          "Tab"
        ) {

          trapFocus(
            event
          );

        }

      }
    );


    /* ==================================================
       ACTUALIZACIONES DE SESIÓN
    ================================================== */

    window.addEventListener(
      "peguu:customer-authenticated",
      () => {

        updateHeaderAuthState();


        fillAccountView();

      }
    );


    window.addEventListener(
      "peguu:customer-logged-out",
      updateHeaderAuthState
    );


    window.addEventListener(
      "storage",
      (
        event
      ) => {

        if (
          event.key !==
          AUTH_STORAGE_KEY
        ) {

          return;

        }


        updateHeaderAuthState();


        if (
          isCustomerAuthenticated()
        ) {

          fillAccountView();

        } else if (
          activeViewName ===
          "account" &&
          root.classList.contains(
            "is-open"
          )
        ) {

          showView(
            "login"
          );

        }

      }
    );


    /* ==================================================
       API PÚBLICA
    ================================================== */

    window.PeguuAuth = {

      open:
        openModal,

      close:
        closeModal,

      showView,

      getSession() {

        return readCustomerSession();

      },

      isAuthenticated() {

        return isCustomerAuthenticated();

      },

      login:
        loginCustomer,

      startShopifyLogin:
        redirectToShopifyLogin,

      loginWithShopify:
        loginCustomerWithShopify,

      authFetch:
        window.PeguuAuthClient?.authFetch,

      getToken() {
        return window.PeguuAuthClient?.getToken?.() || "";
      },

      getCustomer() {
        return window.PeguuAuthClient?.getCustomer?.() ||
          readCustomerSession()?.customer ||
          null;
      },

      validateSession:
        validateCustomerSession,

      async logout() {

        if (
          window.PeguuAuthClient &&
          typeof window.PeguuAuthClient.logout ===
            "function"
        ) {
          await window.PeguuAuthClient.logout();
        } else {
          removeCustomerSession();
        }

        closeModal();

      },

      refresh() {

        updateHeaderAuthState();


        fillAccountView();

      },

      isEmailVerified() {

        return isCustomerEmailVerified(
          readCustomerSession()
            ?.customer
        );

      },

      refreshCustomer:
        refreshCustomerProfile,

      requestEmailVerification,

    };


    /* ==================================================
       ESTADO INICIAL
    ================================================== */

    updateHeaderAuthState();


    fillAccountView();


    resetPasswordVisibility();


    const currentUrl =
      new URL(
        window.location.href
      );


    if (
      currentUrl.searchParams.get(
        "peguu-auth"
      ) === "login"
    ) {

      currentUrl.searchParams.delete(
        "peguu-auth"
      );


      window.history.replaceState(
        window.history.state,
        "",
        `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
      );


      openModal(
        "login"
      );

    }


    synchronizeShopifySession();

  }


  /* ==================================================
     INICIALIZAR TODOS LOS MODALES
  ================================================== */

  function initializeAllAuthModals(
    scope = document
  ) {

    const roots =
      scope.matches?.(
        "[data-peguu-auth]"
      )
        ? [scope]
        : Array.from(
            scope.querySelectorAll(
              "[data-peguu-auth]"
            )
          );


    roots.forEach(
      initializeAuthModal
    );


    updateHeaderAuthState();

  }


  /* ==================================================
     DOM READY
  ================================================== */

  if (
    document.readyState ===
    "loading"
  ) {

    document.addEventListener(
      "DOMContentLoaded",
      () => {

        initializeAllAuthModals();

      }
    );

  } else {

    initializeAllAuthModals();

  }


  /* ==================================================
     SHOPIFY THEME EDITOR
  ================================================== */

  document.addEventListener(
    "shopify:section:load",
    (
      event
    ) => {

      initializeAllAuthModals(
        event.target
      );

    }
  );

})();
